// services/signalr.service.js
import { apiConfig } from '@/utils/config';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import webrtcService from './webrtc.service';
import { decryptData } from '@/utils/crypto';
import { CallPersistentRetryPolicy } from '@/utils/signalRRetryPolicy';

class SignalRService {
    constructor() {
        this.connection = null;
        this.callId = null;
        this.connectedOperators = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    async connect() {
        try {
            const baseURL = apiConfig.baseUrl.replace('/v1/', '');
            const accessToken = decryptData(localStorage.getItem('token'));
            this.connection = new HubConnectionBuilder()
                .withUrl(`${baseURL}/rtcHub`, {
                    accessTokenFactory: () => accessToken,
                })
                .withAutomaticReconnect(new CallPersistentRetryPolicy()) // 🆕 Exponential backoff
                .configureLogging(LogLevel.Information)
                .build();

            this.setupCallbacks();
            this.setupConnectionCallbacks(); // 🆕 Connection state handling

            await this.connection.start();
            console.log('✅ SignalR connected successfully as operator');
            this.reconnectAttempts = 0; // Reset on successful connection

        } catch (error) {
            console.error('❌ Failed to connect to SignalR:', error);
            this.handleConnectionError(error);
            throw error;
        }
    }

    setupCallbacks() {
        // 🆕 IMPROVED: Listen for new emergency calls (for operators)
        this.connection.on("NewEmergencyCall", (call) => {
            console.log('📞 New emergency call received:', call);
            this.onNewEmergencyCall(call);
        });

        this.connection.on("IncomingEmergencyCall", (call) => {
            this.onIncomingEmergencyCall(call);
        })

        this.connection.on("CallStatusChanged", (call) => {
            this.onCallStatusChanged(call);
        });

        // 🆕 IMPROVED: Listen for call updates
        this.connection.on("CallUpdated", (call) => {
            console.log('🔄 Call updated:', call);
            this.onCallUpdated(call);
        });

     

        // 🆕 IMPROVED: Listen for call ended
        this.connection.on("CallEnded", (callId) => {
            console.log('❌ Call ended:', callId);
            this.onCallEnded(callId);
        });

        this.connection.on("OperatorStatusChanged", (state) => {
            console.log('Operator Status Changed:', state);
        })

        this.connection.on("ResponderLocationUpdated",(payload)=>{
            this.onResponderLocationUpdated(payload);
        })

        // 🆕 IMPROVED: Listen for operator joined (for tracking)
        this.connection.on("OperatorJoined", (operatorConnectionId) => {
            console.log('🎧 Operator joined call:', operatorConnectionId);
            this.connectedOperators.add(operatorConnectionId);
            this.onOperatorJoined(operatorConnectionId);
        });

        // WebRTC signaling - Handle incoming offers (from clients)
        this.connection.on("ReceiveOffer", async (fromConnectionId, offer) => {
            console.log('📨 Received WebRTC offer from client:', fromConnectionId);
            try {
                const answer = await webrtcService.handleOffer(offer, fromConnectionId);
                await this.sendAnswer(fromConnectionId, answer);
                console.log('✅ Sent WebRTC answer to client');
            } catch (error) {
                console.error('❌ Failed to handle offer:', error);
                this.handleWebRTCError('handleOffer', error);
            }
        });

        // WebRTC signaling - Handle answers (from clients)
        this.connection.on("ReceiveAnswer", async (fromConnectionId, answer) => {
            console.log('📨 Received WebRTC answer from client:', fromConnectionId);
            try {
                await webrtcService.handleAnswer(answer);
                console.log('✅ WebRTC answer processed');
            } catch (error) {
                console.error('❌ Failed to handle answer:', error);
                this.handleWebRTCError('handleAnswer', error);
            }
        });

        // WebRTC signaling - Handle ICE candidates
        this.connection.on("ReceiveIceCandidate", async (fromConnectionId, candidate) => {
            console.log('📨 Received ICE candidate from:', fromConnectionId);
            try {
                await webrtcService.addIceCandidate(candidate);
            } catch (error) {
                console.error('❌ Failed to add ICE candidate:', error);
                // Don't throw - ICE candidates can fail without breaking call
            }
        });
    }

    // 🆕 NEW: Connection state handling
    setupConnectionCallbacks() {
        this.connection.onreconnecting((error) => {
            console.log('🔄 SignalR reconnecting...', error);
            this.reconnectAttempts++;
            this.onConnectionStateChange('reconnecting');
        });

        this.connection.onreconnected((connectionId) => {
            console.log('✅ SignalR reconnected:', connectionId);
            this.reconnectAttempts = 0;
            this.onConnectionStateChange('connected');
        });


        this.connection.onclose((error) => {
            console.log('🔌 SignalR connection closed:', error);
            this.onConnectionStateChange('disconnected');

            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                console.log(`Attempting to reconnect (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})...`);
                setTimeout(() => this.connect(), 2000 * this.reconnectAttempts);
            }
        });
    }


    // Operator joins a call
    async joinCallAsOperator(callId) {
        try {
            this.callId = callId;
            console.log('🎧 Operator joining call:', callId);
            await this.connection.invoke("AcceptEmergencyCall", callId);
            console.log('✅ Successfully joined call as operator');
        } catch (error) {
            console.error('❌ Failed to join call as operator:', error);
            throw error;
        }
    }

     // Operator joins a call
    async rejectEmergencyCall(callId) {
        try {
            console.log('🎧 Rejecting call:', callId);
            await this.connection.invoke("RejectEmergencyCall", callId);
            this.callId = null;

            console.log('✅ Call has been rejected');
        } catch (error) {
            console.error('❌ Failed to reject the call:', error);
            throw error;
        }
    }


    // Get active calls from server
    async getActiveCalls() {
        if (!this.connection) {
            throw new Error('Not connected to SignalR. Please connect first.');
        }

        try {
            console.log('📋 Fetching active calls from server...');
            const activeCalls = await this.connection.invoke("GetActiveCalls");
            console.log('✅ Active calls received:', activeCalls);
            return activeCalls;
        } catch (error) {
            console.error('❌ Failed to get active calls:', error);
            throw error;
        }
    }

    async acceptEmergencyCall(callId) {
        if (!this.connection) {
            throw new Error('Not connected to SignalR. Please connect first.');
        }

        try {
            console.log('📋 Fetching active calls from server...');
            await this.connection.invoke("AcceptEmergencyCall", callId);
            console.log('✅ Active calls accepted:', callId);
            return true;
        } catch (error) {
            console.error('❌ Failed to get active calls:', error);
            throw error;
        }
    }

    // WebRTC signaling - Send offer to client
    async sendOffer(targetConnectionId, offer) {
        try {
            console.log('📤 Sending WebRTC offer to client:', targetConnectionId);
            await this.connection.invoke("SendOffer", this.callId, targetConnectionId, offer);
            console.log('✅ WebRTC offer sent successfully');
        } catch (error) {
            console.error('❌ Failed to send offer:', error);
            throw error;
        }
    }

    // WebRTC signaling - Send answer to client
    async sendAnswer(targetConnectionId, answer) {
        try {
            console.log('📤 Sending WebRTC answer to client:', targetConnectionId);
            await this.connection.invoke("SendAnswer", this.callId, targetConnectionId, answer);
            console.log('✅ WebRTC answer sent successfully');
        } catch (error) {
            console.error('❌ Failed to send answer:', error);
            throw error;
        }
    }

    // WebRTC signaling - Send ICE candidate
    async sendIceCandidate(candidate, targetConnectionId = null) {
        try {
            if (targetConnectionId) {
                console.log('📤 Sending ICE candidate to:', targetConnectionId);
                await this.connection.invoke("SendIceCandidate", this.callId, targetConnectionId, candidate);
            } else {
                console.log('📤 Sending ICE candidate to all in call');
                await this.connection.invoke("SendIceCandidateToAll", this.callId, candidate);
            }
        } catch (error) {
            console.error('❌ Failed to send ICE candidate:', error);
            // Don't throw - ICE candidates are best effort
        }
    }

    // End current call
    async endCall(callId) {
        try {
            this.callId = callId;
            if (this.callId) {
                console.log('❌ Ending call:', this.callId);
                await this.connection.invoke("EndCall", this.callId);
                this.callId = null;
                this.connectedOperators.clear();
                console.log('✅ Call ended successfully');
            }
        } catch (error) {
            console.error('❌ Failed to end call:', error);
            throw error;
        }
    }

    // 🆕 NEW: Connection error handling
    // eslint-disable-next-line
    handleConnectionError(error) {
        this.reconnectAttempts++;

        if (this.reconnectAttempts <= this.maxReconnectAttempts) {
            console.log(`Retrying connection (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            setTimeout(() => this.connect(), 2000 * this.reconnectAttempts);
        } else {
            console.error('Max reconnection attempts reached');
            this.onConnectionError('Max reconnection attempts reached');
        }
    }

    // 🆕 NEW: WebRTC error handling
    handleWebRTCError(context, error) {
        console.error(`WebRTC error in ${context}:`, error);
        const event = new CustomEvent('webrtcError', {
            detail: { context, error: error.message }
        });
        window.dispatchEvent(event);
    }

    // Event handlers that components can listen to
    onNewEmergencyCall(call) {
        const event = new CustomEvent('newEmergencyCall', { detail: call });
        window.dispatchEvent(event);
    }

    onIncomingEmergencyCall(call) {
        const event = new CustomEvent('incomingEmergencyCall', { detail: call });
        window.dispatchEvent(event);
    }

    onCallStatusChanged(call) {
        const event = new CustomEvent('callStatusChanged', { detail: call });
        window.dispatchEvent(event);
    }

    onCallUpdated(call) {
        const event = new CustomEvent('callUpdated', { detail: call });
        window.dispatchEvent(event);
    }

    onCallEnded(callId) {
        if (callId) {
            const event = new CustomEvent('callEnded', { detail: callId });
            window.dispatchEvent(event);
        }
    }

    onResponderLocationUpdated(payload){
       // console.log(payload);
        const event = new CustomEvent('responderLocationUpdated',{detail: payload});
        window.dispatchEvent(event);
    }
  

    onOperatorJoined(operatorConnectionId) {
        const event = new CustomEvent('operatorJoined', { detail: operatorConnectionId });
        window.dispatchEvent(event);
    }

    // 🆕 NEW: Connection state change handler
    onConnectionStateChange(state) {
        const event = new CustomEvent('signalrConnectionState', { detail: state });
        window.dispatchEvent(event);
    }

    // 🆕 NEW: Connection error handler
    onConnectionError(error) {
        const event = new CustomEvent('signalrConnectionError', { detail: error });
        window.dispatchEvent(event);
    }

    // 🆕 NEW: Utility method to check connection status
    getConnectionState() {
        if (!this.connection) return 'disconnected';
        return this.connection.state;
    }

    // 🆕 NEW: Check if connected
    isConnected() {
        return this.connection && this.connection.state === 'Connected';
    }

    // 🆕 NEW: Get connection statistics
    getConnectionStats() {
        return {
            // eslint-disable-next-line
            state: this.getConnectionState(),
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: this.maxReconnectAttempts,
            connectedOperators: this.connectedOperators.size,
            currentCallId: this.callId
        };
    }
}


// Freeze the instance to prevent modification

export default new SignalRService();